/**
 * Color Harmony Engine — palette roles, harmony generation, contrast checks.
 *
 * From a seed color and a harmony rule it derives the six palette roles the
 * rest of the engine consumes: primary, secondary, accent, neutral,
 * background, text. Text colors are contrast-validated (WCAG relative
 * luminance) and auto-corrected to black/white when the requested color
 * would be unreadable on its background.
 */

// ── Color math (hex ⇄ hsl, luminance) ───────────────────────────────────────

export const hexToRgb = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) throw new Error(`Invalid hex color "${hex}"`);
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
};

const rgbToHex = ({ r, g, b }) =>
  '#' + [r, g, b].map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('');

const rgbToHsl = ({ r, g, b }) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return { h: h * 360, s, l };
};

const hslToRgb = ({ h, s, l }) => {
  h = ((h % 360) + 360) % 360 / 360;
  if (s === 0) { const v = l * 255; return { r: v, g: v, b: v }; }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const f = (t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return { r: f(h + 1 / 3) * 255, g: f(h) * 255, b: f(h - 1 / 3) * 255 };
};

const shift = (hex, { h = 0, s = 0, l = 0 }) => {
  const hsl = rgbToHsl(hexToRgb(hex));
  return rgbToHex(hslToRgb({
    h: hsl.h + h,
    s: Math.max(0, Math.min(1, hsl.s + s)),
    l: Math.max(0, Math.min(1, hsl.l + l)),
  }));
};

/** WCAG relative luminance of a hex color. */
export const relativeLuminance = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  const lin = (v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};

/** WCAG contrast ratio between two hex colors (1–21). */
export const contrastRatio = (a, b) => {
  const la = relativeLuminance(a), lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
};

/** Pick black or white — whichever reads better on `background`. */
export const readableTextOn = (background) =>
  contrastRatio('#111111', background) >= contrastRatio('#ffffff', background)
    ? '#111111'
    : '#ffffff';

/**
 * Nudge a color's lightness (away from the background's) until it clears
 * `minRatio` contrast. Preserves hue — used to keep accents on-brand but
 * readable instead of swapping them for black/white.
 */
export const ensureContrast = (color, background, minRatio = 3.0) => {
  const darkerBg = relativeLuminance(background) < 0.5;
  let current = color;
  for (let i = 0; i < 20 && contrastRatio(current, background) < minRatio; i++) {
    current = shift(current, { l: darkerBg ? 0.04 : -0.04 });
  }
  return current;
};

// ── Harmony generation ───────────────────────────────────────────────────────

export const HARMONIES = ['complementary', 'analogous', 'triadic', 'monochrome'];

const HARMONY_HUES = {
  complementary: [0, 180, 180],
  analogous: [0, 30, -30],
  triadic: [0, 120, 240],
  monochrome: [0, 0, 0],
};

/**
 * Build the palette roles from a seed color.
 * options = { seed: '#hex', harmony, dark = false }
 * Returns { harmony, roles: { primary, secondary, accent, neutral,
 * background, text }, contrast: { textOnBackground, textOnPrimary } }
 */
export const generatePalette = ({ seed, harmony = 'complementary', dark = false } = {}) => {
  if (!seed) throw new Error('generatePalette: seed color required');
  if (!HARMONIES.includes(harmony)) throw new Error(`Unknown harmony "${harmony}"`);
  const [h1, h2, h3] = HARMONY_HUES[harmony];

  const primary = shift(seed, { h: h1 });
  const secondary = harmony === 'monochrome'
    ? shift(seed, { l: -0.18 })
    : shift(seed, { h: h2, s: -0.08 });
  const rawAccent = harmony === 'monochrome'
    ? shift(seed, { l: 0.22, s: 0.1 })
    : shift(seed, { h: h3, s: 0.05, l: 0.05 });
  const neutral = shift(seed, { s: -0.85, l: dark ? -0.2 : 0.25 });
  const background = dark ? shift(seed, { s: -0.5, l: -0.42 }) : shift(seed, { s: -0.6, l: 0.42 });
  const text = readableTextOn(background);
  const accent = ensureContrast(rawAccent, background, 3.0);

  return {
    harmony,
    dark,
    roles: { primary, secondary, accent, neutral, background, text },
    contrast: {
      textOnBackground: Number(contrastRatio(text, background).toFixed(2)),
      textOnPrimary: Number(contrastRatio(readableTextOn(primary), primary).toFixed(2)),
    },
  };
};

/**
 * Validate a palette's readability. Returns { valid, issues[] } where each
 * issue is { pair, ratio, required }. AA large-text threshold (3.0) is used —
 * flyer type is display-sized.
 */
export const validatePaletteContrast = (roles, minRatio = 3.0) => {
  const issues = [];
  const check = (fg, bg, pair) => {
    const ratio = contrastRatio(roles[fg], roles[bg]);
    if (ratio < minRatio) issues.push({ pair, ratio: Number(ratio.toFixed(2)), required: minRatio });
  };
  check('text', 'background', 'text/background');
  check('accent', 'background', 'accent/background');
  return { valid: issues.length === 0, issues };
};
